// src/utils/apiSlice.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { toast } from "react-toastify";
import SERVICE_BASE_URLS from "./apiConfig";
import { logout } from "./authSlice";
import { broadcastLogout } from "./crossTabAuthSync";
import globalRouter from "./globalRouter";
// Base query with token
let lastToastMessage = "";
let lastToastAt = 0;

const dynamicBaseQuery = fetchBaseQuery({
  prepareHeaders: (headers, { getState, endpoint }) => {
    // Endpoints that don't need a Bearer token
    const publicEndpoints = [
      "login",
      "loginWithGoogle",
      "register",
      "sendotp",
      "generateEmailOtp",
      "verifyotp",
      "sendPhoneOtp",
      "verifyPhoneOtp",
      "forgotPassword"
    ];

    if (publicEndpoints.includes(endpoint)) {
      return headers;
    }

    // Redux state fetch (often null without proper authSlice injection)
    let token = getState()?.auth?.token;

    // Fallback directly to localStorage/sessionStorage where login saves it 
    if (!token) {
      token = localStorage.getItem("token") || sessionStorage.getItem("token");
    }

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

export const apiSlice = createApi({
  reducerPath: "api",
  refetchOnFocus: false,
  refetchOnReconnect: false,
  /** Refetch when patientId/userId args change (e.g. profile switch) */
  refetchOnMountOrArgChange: true,
  baseQuery: async (args, api, extraOptions) => {
    let url = "";
    let service = "employee";
    let method = "GET";
    let body;

    if (typeof args === "string") {
      url = args;
    } else {
      url = args.url;
      service = args.service || service;
      method = args.method || method;
      body = args.body;
    }

    const result = await dynamicBaseQuery(
      {
        url: `${SERVICE_BASE_URLS[service]}${url}`,
        method,
        body,
      },
      api,
      extraOptions
    );


    if (result?.error) {
      console.log(result)
      const { status, data, error } = result.error;

      let message = "Something went wrong";

      if (typeof data === "string") {
        message = data;
      } else if (data?.message) {
        message = data.message;
      } else if (data?.detail) {
        message = data.detail;
      } else if (error) {
        message = error;
      }


      if (
        status === 401 ||
        data?.message === "Unauthorized"
      ) {
        api.dispatch(logout());
        broadcastLogout();
        // api.dispatch(apiSlice.util.resetApiState());
        globalRouter.navigate("/login");


      } else {
        // Abort/cancelled requests are user-driven (e.g. "Stop generating") and should be silent.
        const abortLike =
          error === "AbortError" ||
          (typeof error === "string" && error.toLowerCase().includes("aborterror")) ||
          (typeof data === "string" && data.toLowerCase().includes("aborterror")) ||
          (typeof message === "string" &&
            message.toLowerCase().includes("aborterror")) ||
          (typeof message === "string" &&
            message.toLowerCase().includes("signal is aborted"));

        if (abortLike) {
          return {
            error: {
              status,
              data: message,
            },
          };
        }

        // Dedupe toasts: same message within a short time window can be triggered twice
        // (e.g., RTK Query retry + component-level catch).
        const now = Date.now();
        const normalizedMessage =
          typeof message === "string" ? message.trim().toLowerCase() : String(message);

        // Some flows handle these messages in the UI already (no toast needed).
        if (
          normalizedMessage.includes("otp already verified") &&
          (normalizedMessage.includes("reset your password") ||
            normalizedMessage.includes("reset password"))
        ) {
          return {
            error: {
              status,
              data: message,
            },
          };
        }

        // Registration flow wants to show this toast only on Register page.
        // So we suppress it globally and let the component decide.
        if (
          normalizedMessage.includes("account with this email already exists") ||
          normalizedMessage.includes("account with this phone number already exists") ||
          normalizedMessage.includes("account with this mobile already exists") ||
          normalizedMessage.includes("account already exists")
        ) {
          return {
            error: {
              status,
              data: message,
            },
          };
        }

        const toastKey = `apiSlice:error:${normalizedMessage}`;
        const isDuplicate =
          normalizedMessage === lastToastMessage && now - lastToastAt < 3000;

        if (!isDuplicate) {
          toast.error(message, { toastId: toastKey });
          lastToastMessage = normalizedMessage;
          lastToastAt = now;
        }
      }


      return {
        error: {
          status,
          data: message,
        },
      };
    }


    return {
      data: result.data,
    };
  },

  tagTypes: [
    "Items",
    "Patients",
    "Appointments",
    "FamilyMembers",
    "Allergies",
    "medication",
    "chat",
    "dashboardData",
    "chat-summary",
    "ProfileSwitch",
    "Vitals",
  ],
  endpoints: (builder) => ({
    // GET Requests
    getallergiesChronicDeasese: builder.query({
      query: (searchText) => ({
        url: `icd10/search?query=${searchText}`,
        service: "common",
      }),
      providesTags: ["Allergies"],
    }),
    getmedicationSupplement: builder.query({
      query: (searchText) => ({
        url: `medications/search?keyword=${searchText}`,
        service: "common",
      }),
      providesTags: ["medication"],
    }),

    getPatients: builder.query({
      query: () => ({ url: "patients", service: "common" }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: "Patients", id })),
            { type: "Patients", id: "LIST" },
          ]
          : [{ type: "Patients", id: "LIST" }],
    }),
    getAppointments: builder.query({
      query: (patientId) => ({ url: `appointments/getAppointment/${patientId}`, service: "common" }),
      providesTags: ["Appointments", "ProfileSwitch"],
    }),

    // POST Requests
    login: builder.mutation({
      query: (data) => ({
        url: "login",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["login"],
    }),
    /** POST auth base + login/google — body: { idToken } (Google OIDC JWT). Adjust url if your API differs. */
    loginWithGoogle: builder.mutation({
      query: (data) => ({
        url: "google",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["login", "Gamification"],
    }),
    register: builder.mutation({
      query: (data) => ({
        url: "signup",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["signup"],
    }),
    forgotPassword: builder.mutation({
      query: (data) => ({
        url: "forgot-password",
        service: "auth",
        method: "POST",
        body: data,
      }),
    }),

    checkExistOrNot: builder.mutation({
      query: (data) => ({
        url: "users/name-by-phone",
        service: "base",
        method: "POST",
        body: data,
      }),
    }),

    chatbot: builder.mutation({
      query: (data) => ({
        url: "chat",
        service: "dsApis",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),

    chatbotWithoutAuth: builder.mutation({
      query: (data) => ({
        url: "chat/auth",
        service: "dsApis",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),

    sendotp: builder.mutation({
      query: (data) => ({
        url: "otp/generate",
        service: "common",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),

    /** Auth service (REACT_APP_AUTH_SERVICE_URL): POST generate-email-otp — body `{ email }` only. */
    generateEmailOtp: builder.mutation({
      query: ({ email }) => ({
        url: "generate-email-otp",
        service: "auth",
        method: "POST",
        body: { email: String(email ?? "").trim() },
      }),
    }),

    verifyotp: builder.mutation({
      query: (data) => ({
        url: "otp/verify",
        service: "common",
        method: "POST",
        body: data,
      }),
    }),

    verifyEmailOtp: builder.mutation({
      query: (data) => ({
        url: "email/verify-otp",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Gamification"],
    }),

    sendPhoneOtp: builder.mutation({
      query: (data) => ({
        url: "mobile/send-otp",
        service: "auth",
        method: "POST",
        body: data,
      }),
    }),
    verifyPhoneOtpForRegister: builder.mutation({
      query: (data) => ({
        url: "otp/verify/mobile",
        service: "common",
        method: "POST",
        body: data,
      }),
    }),

    verifyPhoneOtp: builder.mutation({
      query: (data) => ({
        url: "mobile/verify-otp",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Gamification"],
    }),

    extractReport: builder.mutation({
      query: (data) => ({
        url: "extract/document",
        service: "dsApis2",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),


    extractReportauth: builder.mutation({
      query: (data) => ({
        url: "extract/document/auth",
        service: "dsApis2",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
      transformErrorResponse: (response) => {
        return response.data; // Pass error data forward
      },
    }),



    addFamilyMember: builder.mutation({
      query: (data) => ({
        url: "user-relationships/addFamilyMember",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["FamilyMembers", "Gamification"],
    }),
    /** PUT user-relationships/{relatedUserId} — body: relationship fields from list row */
    updateFamilyMember: builder.mutation({
      query: ({ relatedUserId, ...body }) => ({
        url: `user-relationships/${relatedUserId}`,
        service: "common",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FamilyMembers"],
    }),
    createPatient: builder.mutation({
      query: (data) => ({
        url: "patients",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Patients", id: "LIST" }, "Gamification"],
    }),

    updatePatient: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `patients/${id}`,
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Patients", id },
      ],
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedPatient } = await queryFulfilled;

          // Update the specific profile detail cache
          dispatch(
            apiSlice.util.updateQueryData("getProfileDetail", id, (draft) => {
              Object.assign(draft, updatedPatient);
            })
          );

          // Update the patients list cache if it exists
          dispatch(
            apiSlice.util.updateQueryData("getPatients", undefined, (draft) => {
              const index = draft.findIndex((p) => p.id === id);
              if (index !== -1) {
                draft[index] = { ...draft[index], ...updatedPatient };
              }
            })
          );
        } catch { }
      },
    }),
    createAppointment: builder.mutation({
      query: (data) => ({
        url: "appointments/create",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appointments"],
    }),



    getdashboardData: builder.query({
      query: (patientId) => ({ url: `dashboard/getDashboard?patientId=${patientId}`, service: "common" }),
      providesTags: ["dashboardData", "ProfileSwitch"],
    }),


    getChatSummaryList: builder.query({
      query: (userId) => ({ url: `summary/user/${userId}`, service: "dsApis" }),
      providesTags: ["chat-summary", "ProfileSwitch"],
    }),

    getChatSummaryLogs: builder.query({
      query: (userId) => ({ url: `history/${userId}`, service: "dsApis" }),
      providesTags: ["chat-summary", "ProfileSwitch"],
    }),
    getMedicationSchedule: builder.query({
      query: ({ patientId, startDate, endDate }) => ({
        url: `drugIntake/drug-intake?patientId=${patientId}&startDate=${startDate}&endDate=${endDate}`,
        service: "common"
      }),
      providesTags: ["ProfileSwitch"],
    }),
    createMedications: builder.mutation({
      query: (data) => ({
        url: "drugIntake/schedule",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appointments", "ProfileSwitch", "medication"],
    }),

    gemifications: builder.query({
      query: (data) => ({
        url: `gamification/${data}`,
        service: "base",
        method: "GET",

      }),
      providesTags: ["Gamification"],

    }),




    getHealthCareSummary: builder.query({
      query: (patientId) => ({
        url: `health-summary/user/${patientId}`,

        service: "dsApis2",
        method: "GET",
      }),
      providesTags: ["ProfileSwitch"],
    }),
    getPatientList: builder.query({
      query: (primaryUserId) => ({
        url: `user-relationships/related-users?primaryUserId=${primaryUserId}`,

        service: "common",
        method: "GET",
      }),
      providesTags: ["FamilyMembers"],
    }),


    getProfileDetail: builder.query({
      query: (userID) => ({
        url: `patients/${userID}`,
        service: "common",
        method: "GET",
      }),
      providesTags: (result, error, id) => [
        { type: "Patients", id },
        "ProfileSwitch",
      ],
    }),

    // Inside apiSlice.ts
    getVitalsReport: builder.query({
      query: ({ patientId, vitalType }) => ({
        // Manually attach the query parameter if 'params' is being ignored
        url: `vitals/patient/${patientId}${vitalType ? `?vitalType=${vitalType}` : ''}`,
        service: "apiV1",
        method: "GET",
      }),
      providesTags: (result, error, { vitalType }) => [
        { type: "Vitals", id: vitalType || 'ALL' },
      ],
    }),
    getHealthCareSummaryGraphs: builder.query({
      query: (userId) => ({
        url: `health-summary/${userId}?startDate=2025-03-10&endDate=2027-03-20`,

        service: "apiV1",
        method: "GET",
      }),
      providesTags: ["FamilyMembers", "ProfileSwitch"],
    }),
    getPatientLabReports: builder.query({
      query: ({ patientId, documentCategory, page = 0, size = 10 }) => ({
        url: `lab-reports/patient/${patientId}?documentCategory=${encodeURIComponent(
          documentCategory
        )}&page=${page}&size=${size}`,
        service: "common",
        method: "GET",
      }),
      providesTags: ["ProfileSwitch"],
    }),

    addEvents: builder.mutation({
      query: (data) => ({
        url: `calendar-events/${data.patientId}`,
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Events"],
    }),

    markTaken: builder.mutation({
      query: (data) => ({
        url: `drugIntake/mark-taken?patientId=${data.patientId}&drugIntakeProgressId=${data.drugIntakeProgressId}`,
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appointments", "ProfileSwitch", "medication", "Gamification"],
    }),

    ListingEvents: builder.query({
      query: (data) => ({
        url: `calendar-events/${data.patientId}?appointmentType=${data.appointmentType}&startDate=${data.startDate}&endDate=${data.endDate}`,

        service: "common",
        method: "GET",
      }),
      providesTags: ["Events"],
    }),
    medicationSingleView: builder.query({
      query: (patientId) => ({
        url: `patient-medications/summary/${patientId}`,
        method: "GET",
        service: "common",
      }),
      providesTags: ["Events"],
    }),
  }),





});

// Export hooks
export const {
  useGetHealthCareSummaryGraphsQuery,
  useGetVitalsReportQuery,
  useGetProfileDetailQuery,
  useGetPatientListQuery,
  useGetHealthCareSummaryQuery,
  useCreateMedicationsMutation,
  useGetMedicationScheduleQuery,
  useGetChatSummaryLogsQuery,
  useGetallergiesChronicDeaseseQuery,
  useGetmedicationSupplementQuery,
  useLoginMutation,
  useLoginWithGoogleMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useChatbotMutation,
  useChatbotWithoutAuthMutation,
  useGetChatSummaryListQuery,
  useSendotpMutation,
  useGenerateEmailOtpMutation,
  useVerifyotpMutation,
  useVerifyEmailOtpMutation,
  useSendPhoneOtpMutation,
  useVerifyPhoneOtpMutation,
  useExtractReportMutation,
  useAddFamilyMemberMutation,
  useUpdateFamilyMemberMutation,
  useGetPatientsQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useGetAppointmentsQuery,
  useCreateAppointmentMutation,
  useExtractReportauthMutation,
  useGetdashboardDataQuery,
  useGetPatientLabReportsQuery,
  useVerifyPhoneOtpForRegisterMutation,
  useCheckExistOrNotMutation,
  useGemificationsQuery,
  useAddEventsMutation,
  useMarkTakenMutation,
  useListingEventsQuery,
  useMedicationSingleViewQuery
} = apiSlice;
